import React from 'react'
import PropTypes from 'prop-types'
import LazyLoad from 'react-lazyload'
import CreditCardDebtSimple from './CreditCardDebtSimple'
import MoreQuestions from './MoreQuestions'
import Faqs from 'src/components/pages/Home/Faqs'
import DebtReliefCalculator from './DebtReliefCalculatorV3'
import DebtReliefCalculatorMobile from './DebtReliefCalculatorV2'
import DataVisualization from './DataVisualization'

import { useFeatureFlags } from 'src/common/ab-testing/context'
import { featureToggles } from 'src/common/constants/featureToggles'
import { experimentSectionByVariation } from 'src/common/analytics/consts'
import {
  trustMarkerEntryLink,
  trustMarkerEntryRatingNumber,
  trustMarkerEntryTotalReviewNumber,
} from 'src/common/trustMarkerEntryFields'
import { getPhoneNumber, numberFormat } from 'src/common/utils'
import HeroBannerClientPoints from './HeroBannerClientPoints'
import TrustMarkersCombinationLogos from 'src/components/shared/TrustMarkersCombinationLogos'
import DebtReliefCalculatorV4 from './DebtReliefCalculatorV4'
import { TalkToARealPersonSection } from './TalkToARealPersonSection'

const HomePage = ({
  data,
  isDefaultLang,
  lang,
  MoreQuestionsComponent = MoreQuestions,
  alternateSectionVariants = false,
}) => {
  const { getExperimentVariation } = useFeatureFlags()

  const homepageSimpleCalculatorTest = getExperimentVariation(
    experimentSectionByVariation[featureToggles.HOMEPAGE_SIMPLE_CALCULATOR_TEST]
  )

  const {
    heroBanner,
    heroPersonV2 = {},
    debtSimple,
    trustMarkersCombination,
    trustPilotInfo,
    debtReliefCalculatorV31: debtReliefCalculator,
    debtReliefCalculatorV4: debtReliefSimpleCalculator,
    cfosData,
    isCfosDisclousuresVariationActive = false,
    useTestimonialFormat = false,
    showCalculator = true,
    showTalkToARealPersonSection = false,
    bannerMessage = 'Freedom debt relief is trusted by 1 million clients and counting',
    heroPerson,
    btnFullWidth = false,
  } = data

  const tpRating = trustMarkerEntryRatingNumber(trustPilotInfo)
  const tpTotal = trustMarkerEntryTotalReviewNumber(trustPilotInfo)

  const newHeroContent = {
    ...heroBanner.content,
    number: getPhoneNumber(true),
    trustPilot: {
      rating: Number.isFinite(tpRating) ? tpRating : 0,
      totalReviews: numberFormat(Number.isFinite(tpTotal) ? tpTotal : 0),
      link: trustMarkerEntryLink(trustPilotInfo),
      color: '#33b083',
      calification: trustPilotInfo?.calification,
      reviews: trustPilotInfo?.reviews,
      prefix: trustPilotInfo?.prefix,
    },
    newTrustpilotItem: true,
  }

  return (
    <div>
      <HeroBannerClientPoints
        useTestimonialFormat={useTestimonialFormat}
        content={newHeroContent}
        isDefaultLang={isDefaultLang}
        heroPerson={heroPerson}
        heroPersonV2={heroPersonV2}
      />

      <TrustMarkersCombinationLogos
        trustMarkersCombination={trustMarkersCombination}
        title={bannerMessage}
      />

      {isDefaultLang && showCalculator ? (
        <LazyLoad offset={145}>
          {isCfosDisclousuresVariationActive ? (
            homepageSimpleCalculatorTest === 'test' ? (
              <DebtReliefCalculatorV4
                data={debtReliefSimpleCalculator}
                cfosData={cfosData}
                isCfosDisclousuresVariationActive
              />
            ) : (
              <>
                <div className="hidden lg:block">
                  <DebtReliefCalculator
                    data={{
                      ...debtReliefCalculator,
                      showWarningSection: !isCfosDisclousuresVariationActive,
                    }}
                    initialAmount={25000}
                    cfosData={cfosData}
                    isCfosDisclousuresVariationActive
                  />
                </div>
                <div className="lg:hidden">
                  <DebtReliefCalculatorMobile
                    data={{
                      title: 'You could free up ',
                      title1: ' each month',
                      buttonText: 'Get a free evaluation',
                      linkText: 'How are these numbers calculated?*',
                    }}
                    initialAmount={25000}
                    cfosData={cfosData}
                    isCfosDisclousuresVariationActive={
                      isCfosDisclousuresVariationActive
                    }
                  />
                </div>
              </>
            )
          ) : (
            <DataVisualization />
          )}
        </LazyLoad>
      ) : null}

      {showTalkToARealPersonSection && <TalkToARealPersonSection />}

      <CreditCardDebtSimple
        title={debtSimple.title}
        debtTitle={debtSimple.debtTitle}
        debtText={debtSimple.debtText}
        saveTitle={debtSimple.saveTitle}
        saveText={debtSimple.saveText}
        enrollTitle={debtSimple.enrollTitle}
        enrollText={debtSimple.enrollText}
        footer={debtSimple.footer}
        button={debtSimple.button}
        btnFullWidth={btnFullWidth}
        variant={alternateSectionVariants ? 'secondary' : 'primary'}
      />
      <Faqs
        lang={lang}
        variant={alternateSectionVariants ? 'primary' : 'secondary'}
      />

      {isDefaultLang && (
        <MoreQuestionsComponent
          phoneNumber={newHeroContent.number}
          variant={alternateSectionVariants ? 'secondary' : 'primary'}
        />
      )}
    </div>
  )
}

HomePage.defaultProps = {
  isDefaultLang: true,
}

HomePage.propTypes = {
  data: PropTypes.object.isRequired,
  isDefaultLang: PropTypes.bool,
}

export default HomePage
